import { useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import { useQueryClient } from "@tanstack/react-query";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClassParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	UNDECLARED_DELAY_BG,
	UNDECLARED_DELAY_HOVER_BG,
} from "@/shared/colors";
import {
	getProductArea,
	PRODUCT_AREA_ASSET_ID,
	Product,
} from "@/shared/models/Product";
import { useTableData } from "../hooks/useTableData";
import { NewProductStatusBar } from "./NewProductStatusBar";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

const ORPHAN_ROW_CLASS = "orphan-product-row";

const getOrphanRowClass = (
	params: RowClassParams<Product>,
): string | undefined => {
	const p = params.data;
	if (!p) return undefined;
	const area = getProductArea(p);
	if (area && area !== "saw" && p.ParentProductId == null)
		return ORPHAN_ROW_CLASS;
	return undefined;
};

const orphanRowStyles = {
	[`& .${ORPHAN_ROW_CLASS}`]: {
		backgroundColor: UNDECLARED_DELAY_BG,
	},
	[`& .${ORPHAN_ROW_CLASS}:hover`]: {
		backgroundColor: `${UNDECLARED_DELAY_HOVER_BG} !important`,
	},
	[`& .${ORPHAN_ROW_CLASS}.ag-row-selected`]: {
		backgroundColor: UNDECLARED_DELAY_HOVER_BG,
	},
};

type Props = {
	isLoading: boolean;
	rows: Product[];
	quickFilterText: string;
	selectedProductId?: number | null;
	defaultAreaId: number;
	onRowClicked: (productId: number | null) => void;
	onEdit?: (productId: number, areaAssetId: number) => void;
};

export const ProductsTable = ({
	isLoading,
	rows,
	quickFilterText,
	selectedProductId,
	defaultAreaId,
	onRowClicked,
	onEdit,
}: Props) => {
	const [showModal, setShowModal] = useState<"create" | "delete" | "">("");
	const [selectedRowData, setSelectedRowData] = useState<Product | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const queryClient = useQueryClient();

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
		defaultAreaId,
		onEdit,
	});

	const statusBar = useMemo(
		() => ({
			statusPanels: [
				{
					statusPanel: NewProductStatusBar,
					statusPanelParams: {
						onNewProduct: () => setShowModal("create"),
					},
					align: "right" as const,
				},
			],
		}),
		[],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "productsContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	useEffect(() => {
		registerConfig({
			id: "productsContext",
			getOptions: getMenuOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Redraw rows when data changes so getRowClass re-evaluates
	useEffect(() => {
		if (gridApi && !isLoading) {
			gridApi.redrawRows();
		}
	}, [rows, gridApi, isLoading]);

	// Skip scroll when the selection originated from an in-table click
	const clickedRowIdRef = useRef<number | null>(null);
	useEffect(() => {
		if (!gridApi || selectedProductId == null) return;

		if (clickedRowIdRef.current === selectedProductId) {
			clickedRowIdRef.current = null;
			return;
		}

		const rowNode = gridApi.getRowNode(`${selectedProductId}`);
		if (rowNode) {
			rowNode.setSelected(true);
			gridApi.ensureNodeVisible(rowNode, "middle");
		}
	}, [gridApi, selectedProductId, rows]);

	return (
		<>
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				sx={{ height: "100%" }}
				spacing={1}
				component={Paper}
				elevation={2}
			>
				<Grid2 size={{ md: 12, xs: 12 }} height={"100%"} sx={orphanRowStyles}>
					<AgGridReact<Product>
						loading={isLoading}
						rowData={rows}
						columnDefs={columnDefs}
						animateRows
						getRowClass={getOrphanRowClass}
						getContextMenuItems={getContextMenuItems}
						onRowClicked={(event) => {
							const id = event.data?.ProductId ?? null;
							clickedRowIdRef.current = id;
							onRowClicked(id);
						}}
						onRowDoubleClicked={(event) => {
							const product = event.data;
							if (product?.ProductId && onEdit) {
								const area = getProductArea(product);
								const areaAssetId = area
									? PRODUCT_AREA_ASSET_ID[area]
									: defaultAreaId;
								onEdit(product.ProductId, areaAssetId);
							}
						}}
						onGridReady={(params) => setGridApi(params.api)}
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(params) => `${params.data.ProductId}`}
						quickFilterText={quickFilterText}
						statusBar={statusBar}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
					/>
				</Grid2>
			</Grid2>
			<UpsertDeleteModal
				product={selectedRowData}
				modal={showModal}
				onHide={(shouldUpdate) => {
					setShowModal("");
					if (shouldUpdate) {
						queryClient.invalidateQueries({ queryKey: ["products"] });
					}
				}}
				onNavigate={(productId) => onRowClicked(productId)}
			/>
		</>
	);
};
